import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { PageRequest } from '../../models/PageRequest'
import { paginate } from '../utils'
import { Role } from '../../models/Role'

const _BASE_URL = import.meta.env.REACT_APP_IAM_API_BASE_URL!

export interface HomeAccount {
  id: string
  name: string
}

export enum CrossAccountGrantStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  REJECTED = 'rejected',
  REVOKED = 'revoked',
  CANCELLED = 'cancelled',
}

export interface CrossAccountGrantOfUser {
  id: string
  targetAccountId: string
  targetAccountName: string
  granterUserId: string
  granterUserName: string
  roleIds: string[]
  status: CrossAccountGrantStatus
  createdAt: string
  updatedAt: string
}

export interface CrossAccountGrant {
  id: string
  targetAccountId: string
  granteeIdpUserId: string
  status: CrossAccountGrantStatus
  roleIds: string[]
  createdAt: string
  updatedAt: string
}

export interface CrossAccountGrantOfGranter {
  id: string
  granterUserId: string
  targetUserId: string
  granteeAccountName: string
  granteeDisplayName: string
  granteeEmail: string
  roleIds: string[]
  status: CrossAccountGrantStatus
  createdAt: string
  updatedAt: string
}

export interface ListGrantsOfCurrentUserRequest {
  statuses?: CrossAccountGrantStatus[]
  targetAccountId?: string
}

export interface GrantOfCurrentUserByIdRequest {
  grantId: string
}

export interface GetGrantByIdRequest extends BaseRequest {
  grantId: string
}

export interface UpdateGrantByIdRequest extends BaseRequest {
  grantId: string
  roleIds: string[]
}

export interface RevokeGrantByIdRequest extends BaseRequest {
  grantId: string
}

export interface GrantCrossAccountAccessRequest extends BaseRequest {
  grantId: string
  roleIds: string[]
}

export interface ListCrossAccountGrantsOfAccountRequest extends BasePageRequest {
  statuses?: CrossAccountGrantStatus[]
}

export const crossAccountApi = createApi({
  reducerPath: 'crossAccountApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['crossAccountGrantOfUser', 'crossAccountGrantOfGranter'],
  endpoints: (builder) => ({
    getHomeAccount: builder.query<HomeAccount, void>({
      query: () => ({ url: '/idp-users/me/cross-accounts/home' }),
      transformResponse: (response: { data: HomeAccount }) => response.data,
    }),
    listGrantsOfCurrentUser: builder.query<BaseResponse<CrossAccountGrantOfUser[]>, ListGrantsOfCurrentUserRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossAccountGrantOfUser>(
          {
            url: '/idp-users/me/cross-account-grants',
            params: {
              ...(request.statuses && { statuses: request.statuses.join(',') }),
              ...(request.targetAccountId && { targetAccountId: request.targetAccountId }),
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'crossAccountGrantOfUser', id }) as const),
              {
                type: 'crossAccountGrantOfUser',
                id: 'LIST',
              },
            ]
          : [{ type: 'crossAccountGrantOfUser', id: 'LIST' }],
    }),
    getGrantOfCurrentUserById: builder.query<CrossAccountGrant, GrantOfCurrentUserByIdRequest>({
      query: (request) => ({
        url: `/idp-users/me/cross-account-grants/${request.grantId}`,
      }),
      transformResponse: (response: { data: CrossAccountGrant }) => response.data,
      providesTags: (_, __, { grantId }) => [{ type: 'crossAccountGrantOfUser', grantId }],
    }),
    acceptGrantOfCurrentUserById: builder.mutation<CrossAccountGrant, GrantOfCurrentUserByIdRequest>({
      query: (request) => ({
        url: `/idp-users/me/cross-account-grants/${request.grantId}/accept`,
        method: 'POST',
      }),
      transformResponse: (response: { data: CrossAccountGrant }) => response.data,
      invalidatesTags: (_, __, { grantId }) => [
        { type: 'crossAccountGrantOfUser', id: grantId },
        {
          type: 'crossAccountGrantOfUser',
          id: 'LIST',
        },
      ],
    }),
    rejectGrantOfCurrentUserById: builder.mutation<CrossAccountGrant, GrantOfCurrentUserByIdRequest>({
      query: (request) => ({
        url: `/idp-users/me/cross-account-grants/${request.grantId}/reject`,
        method: 'POST',
      }),
      transformResponse: (response: { data: CrossAccountGrant }) => response.data,
      invalidatesTags: (_, __, { grantId }) => [
        { type: 'crossAccountGrantOfUser', id: grantId },
        {
          type: 'crossAccountGrantOfUser',
          id: 'LIST',
        },
      ],
    }),
    cancelGrantOfCurrentUserById: builder.mutation<CrossAccountGrant, GrantOfCurrentUserByIdRequest>({
      query: (request) => ({
        url: `/idp-users/me/cross-account-grants/${request.grantId}/cancel`,
        method: 'POST',
      }),
      transformResponse: (response: { data: CrossAccountGrant }) => response.data,
      invalidatesTags: (_, __, { grantId }) => [
        { type: 'crossAccountGrantOfUser', id: grantId },
        {
          type: 'crossAccountGrantOfUser',
          id: 'LIST',
        },
      ],
    }),
    grantCrossAccountRequestToUser: builder.mutation<CrossAccountGrant, GrantCrossAccountAccessRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/cross-account-grants`,
        method: 'POST',
        body: {
          grantId: request.grantId,
          roleIds: request.roleIds,
        },
      }),
      transformResponse: (response: { data: CrossAccountGrant }) => response.data,
      invalidatesTags: [{ type: 'crossAccountGrantOfGranter', id: 'LIST' }],
    }),
    getGrantById: builder.query<CrossAccountGrant, GetGrantByIdRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/cross-account-grants/${request.grantId}`,
      }),
      transformResponse: (response: { data: CrossAccountGrant }) => response.data,
      providesTags: (_, __, { grantId }) => [{ type: 'crossAccountGrantOfGranter', grantId }],
    }),
    updateGrantById: builder.mutation<CrossAccountGrant, UpdateGrantByIdRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/cross-account-grants/${request.grantId}`,
        method: 'PATCH',
        body: {
          roleIds: request.roleIds,
        },
      }),
      transformResponse: (response: { data: CrossAccountGrant }) => response.data,
      invalidatesTags: (_, __, { grantId }) => [
        { type: 'crossAccountGrantOfGranter', id: grantId },
        {
          type: 'crossAccountGrantOfGranter',
          id: 'LIST',
        },
      ],
    }),
    revokeGrantById: builder.mutation<CrossAccountGrant, RevokeGrantByIdRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/cross-account-grants/${request.grantId}/revoke`,
        method: 'POST',
      }),
      transformResponse: (response: { data: CrossAccountGrant }) => response.data,
      invalidatesTags: (_, __, { grantId }) => [
        { type: 'crossAccountGrantOfGranter', id: grantId },
        {
          type: 'crossAccountGrantOfGranter',
          id: 'LIST',
        },
      ],
    }),
    listCrossAccountGrantsOfAccount: builder.query<BasePageResponse<CrossAccountGrantOfGranter[]>, ListCrossAccountGrantsOfAccountRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/cross-account-grants`,
        params: {
          page: request.pageRequest.page,
          size: request.pageRequest.size,
          ...(request.statuses && { statuses: request.statuses.join(',') }),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'crossAccountGrantOfGranter', id }) as const),
              {
                type: 'crossAccountGrantOfGranter',
                id: 'LIST',
              },
            ]
          : [{ type: 'crossAccountGrantOfGranter', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetHomeAccountQuery,
  useListGrantsOfCurrentUserQuery,
  useAcceptGrantOfCurrentUserByIdMutation,
  useRejectGrantOfCurrentUserByIdMutation,
  useCancelGrantOfCurrentUserByIdMutation,
  useGrantCrossAccountRequestToUserMutation,
  useGetGrantByIdQuery,
  useUpdateGrantByIdMutation,
  useRevokeGrantByIdMutation,
  useListCrossAccountGrantsOfAccountQuery,
} = crossAccountApi
